import { useState, type FormEvent } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, Stack, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Link,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendGoogleAdsConversion } from '../utils/googleAds';

export default function LoginPage() {
  const { signIn, signUp, signInWithGoogle, signInWithPhone, confirmPhoneCode, resetPassword, continueAsGuest } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  // Phone auth state
  const [phoneMode, setPhoneMode] = useState<'idle' | 'input' | 'codeSent'>('idle');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isSignUp) {
      sendGoogleAdsConversion('sign_in_click');
    }
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
    } catch (err: any) {
      setError(err.message?.replace('Firebase: ', '') || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message?.replace('Firebase: ', '') || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetSuccess(false);
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (err: any) {
      setResetError(err.message?.replace('Firebase: ', '') || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const phoneDigits = phoneNumber.replace(/\D/g, '');
  const isPhoneValid = phoneDigits.length === 10;

  const handleSendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const e164 = `+1${phoneDigits}`;
      const result = await signInWithPhone(e164);
      setVerificationId(result.verificationId);
      setPhoneMode('codeSent');
    } catch (err: any) {
      setError(err.message?.replace('Firebase: ', '') || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      await confirmPhoneCode(verificationId, otpCode);
    } catch (err: any) {
      setError(err.message?.replace('Firebase: ', '') || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const resetPhoneMode = () => {
    setPhoneMode('idle');
    setPhoneNumber('');
    setOtpCode('');
    setVerificationId('');
    setError('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" textAlign="center" gutterBottom fontWeight={700}>
            AirScribe
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary" mb={3}>
            {phoneMode !== 'idle'
              ? (phoneMode === 'input' ? 'Enter your phone number' : 'Enter the code we sent you')
              : (isSignUp ? 'Create your account' : 'Sign in to continue')}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {phoneMode === 'input' && (
            <Stack spacing={2}>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={resetPhoneMode} sx={{ alignSelf: 'flex-start' }}>
                Back
              </Button>
              <TextField
                label="Phone Number" type="tel" fullWidth
                placeholder="(818) 245-0372"
                value={phoneNumber}
                onChange={e => setPhoneNumber(formatPhoneInput(e.target.value))}
                size="small" autoFocus
                InputProps={{
                  startAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, whiteSpace: 'nowrap' }}>+1</Typography>,
                }}
              />
              <Button variant="contained" fullWidth disabled={loading || !isPhoneValid} onClick={handleSendCode}>
                Send Code
              </Button>
            </Stack>
          )}

          {phoneMode === 'codeSent' && (
            <Stack spacing={2}>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={resetPhoneMode} sx={{ alignSelf: 'flex-start' }}>
                Back
              </Button>
              <Typography variant="body2" color="text.secondary">
                Code sent to {phoneNumber}
              </Typography>
              <TextField
                label="Verification Code" fullWidth
                placeholder="123456"
                value={otpCode} onChange={e => setOtpCode(e.target.value)}
                size="small" autoFocus
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              />
              <Button variant="contained" fullWidth disabled={loading || otpCode.length !== 6} onClick={handleVerifyCode}>
                Verify
              </Button>
            </Stack>
          )}

          {phoneMode === 'idle' && (
            <>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Email" type="email" fullWidth required
                    value={email} onChange={e => setEmail(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Password" type="password" fullWidth required
                    value={password} onChange={e => setPassword(e.target.value)}
                    size="small" inputProps={{ minLength: 6 }}
                  />
                  {!isSignUp && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => setResetDialogOpen(true)}
                        sx={{ cursor: 'pointer' }}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  )}
                  <Button type="submit" variant="contained" fullWidth disabled={loading}>
                    {isSignUp ? 'Sign Up' : 'Sign In'}
                  </Button>
                </Stack>
              </form>

              <Divider sx={{ my: 2 }}>or</Divider>

              <Stack spacing={1}>
                <Button
                  variant="outlined" fullWidth startIcon={<GoogleIcon />}
                  onClick={handleGoogle} disabled={loading}
                >
                  Continue with Google
                </Button>
                <Button
                  variant="outlined" fullWidth startIcon={<PhoneIcon />}
                  onClick={() => { setPhoneMode('input'); setError(''); }} disabled={loading}
                >
                  Continue with Phone
                </Button>
              </Stack>

              <Typography variant="body2" textAlign="center" mt={2}>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <Button size="small" onClick={() => { setIsSignUp(!isSignUp); setError(''); }}>
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </Button>
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth variant="text" color="inherit"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  try { await continueAsGuest(); }
                  catch (err: any) { setError(err.message || 'Failed to start guest session'); }
                  finally { setLoading(false); }
                }}
                sx={{ color: 'text.secondary', fontSize: 13 }}
              >
                Try without signing in
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          {resetSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset email sent! Check your inbox.
            </Alert>
          )}
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={e => setResetEmail(e.target.value)}
            size="small"
            autoFocus
            disabled={resetSuccess}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={loading || resetSuccess}>
            Send Reset Link
          </Button>
        </DialogActions>
      </Dialog>
      <div id="recaptcha-container"></div>
      <Typography variant="caption" color="text.secondary" sx={{ position: 'fixed', bottom: 16, textAlign: 'center', width: '100%', left: 0 }}>
        <Link component={RouterLink} to="/legal/tos" color="inherit" underline="hover">Terms of Service</Link>
        {' · '}
        <Link component={RouterLink} to="/legal/privacy" color="inherit" underline="hover">Privacy Policy</Link>
      </Typography>
    </Box>
  );
}
